import microuniversoApi from "app/api/microuniversoApi"
import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container"
import { PesquisarClienteContainer } from "app/components/pesquisa-cliente/PesquisarClienteContainer"
import { renderizarComponentReact } from "app/components/renderizarOuRemoverComponentReact"
import { useAppSelector } from "app/redux/hooks"
import { IOrcamento } from "app/types/IOrcamento"
import { Vendedor } from "app/types/Vendedor"
import { DadosDaPesquisaOrcamento } from "app/types/pesquisa-orcamento/PesquisaOrcamento"
import { tratamentoErroPadrao } from "app/use-cases"
import { Dict } from "app/utils/Dict"
import globalMU from "globalMU"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Atividade, CadastrarAtendimento, CadastroAtendimento } from "./CadastrarAtendimento"
import { v4 as uuidv4 } from 'uuid';


export type CadastrarAtendimentoContainerProps = {
    onVoltar: () => void;
    passos: Dict<string, () => void>;
    onRemover: () => boolean
    regiaoRenderizada: string
    cadastroAtendimento?: CadastroAtendimento
    foraOrcamento?: boolean // 489353
}

export const CadastrarAtendimentoContainer = (props: CadastrarAtendimentoContainerProps) => {
    const orcamento: IOrcamento = props.foraOrcamento 
                        ? {} 
                        : useAppSelector((store) => store.orcamento)

    const [motivos, setMotivos] = useState([])
    const [atividades, setAtividades] = useState<Atividade[]>([])
    const [vendedores, setVendedores] = useState<Vendedor[]>([])

    const [limparCadastroAtendimento, setLimparCadastroAtendimento] = useState(uuidv4())
    const [cadastroAtendimento, setCadastroAtendimento] = useState<CadastroAtendimento>(props.cadastroAtendimento ?? {
        CodigoCliente: orcamento?.Cliente?.Codigo,
        CodigoVendedor: orcamento.Vendedor?.Codigo,
        NomeVendedor: orcamento.Vendedor?.Nome,
        NomeCliente: orcamento.Cliente?.Nome,
        NumeroPedido: orcamento.Numero ?? 0,
        PermitidoAlterarCliente: false,
    })

    const onRenderizarCadastroAtendimento = () => renderizarComponentReact(<CadastrarAtendimentoContainer {...props} />, props.regiaoRenderizada, true)

    const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, props.passos.map)))
    const isMounted = useRef<boolean>(true)


    const onListarMotivos = useCallback(async (nome: string) => {
        
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.AtendimentoCliente.ListarMotivosPorNome(nome);
            if (!isMounted.current) return
            if (!("HasErrors" in resposta)) {
                setMotivos(resposta)
            }
        } catch (e) {
            if (!isMounted.current) return
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, [])

    const onListarAtividades = useCallback(async (descricao: string) => {
        
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.AtendimentoCliente.ListarAtividadesPorDescricao(descricao);
            if (!isMounted.current) return
            if (!("HasErrors" in resposta)) {
                setAtividades(resposta)
            }
        } catch (e) {
            if (!isMounted.current) return
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, [])

    const onObterDadosCadastro = useCallback(async () => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.AtendimentoCliente.DadosCadastro();
            if (!isMounted.current) return
            setCadastroAtendimento(prev => ({ ...prev, Data: resposta }))
        } catch (e) {
            if (!isMounted.current) return
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, [])

    const onSalvarAtendimento = async (dados: CadastroAtendimento) => {
        try {
            globalMU.blockUI();
            await microuniversoApi.Cliente.CadastrarAtendimento(dados);
            if (!isMounted.current) return
            globalMU.showSuccessMessage("Atendimento atualizado com sucesso")
            onVoltar()
        } catch (e) {
            if (!isMounted.current) return
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }

    const onListarPorNomeVendedor = useCallback(async (nome: string) => {
        
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Vendedor.ListarPorNome(nome);
            if (!isMounted.current) return
            if (!("HasErrors" in resposta)) {
                setVendedores(resposta)
            }
        } catch (e) {
            if (!isMounted.current) return
            tratamentoErroPadrao(e)
        } finally {
            globalMU.unblockUI();
        }
    }, [])

    const onPesquisarCliente = (dadosCadastro: CadastroAtendimento) => {
        renderizarComponentReact(
            <PesquisarClienteContainer
                regiaoRenderizada={props.regiaoRenderizada} passos={passosInterno.current} onVoltar={onRenderizarCadastroAtendimento} consultar
                foraOrcamento={true}
                onSelecionarClienteCallback={(cliente) => {
                    renderizarComponentReact(<CadastrarAtendimentoContainer {...props}
                        cadastroAtendimento={{ ...dadosCadastro, CodigoCliente: cliente.Codigo, NomeCliente: cliente.Nome }} />, props.regiaoRenderizada, true)
                }}
            />, props.regiaoRenderizada, true)
    }

    const onPassoSelecionado = (passo: string) => {
        props.onRemover()
        passosInterno.current.get(passo)()
    }

    const onVoltar = () => {
        props.onRemover()
        props.onVoltar()
    }

    const onLimpar = useCallback(() => {
        setLimparCadastroAtendimento(uuidv4())
        setCadastroAtendimento(prev => ({ Data: prev.Data }))
    }, [])

    useEffect(() => {
        
        passosInterno.current.set("Cadastrar Atendimento", () => onRenderizarCadastroAtendimento)
        onObterDadosCadastro();

        return () => { isMounted.current = false }
    }, [])

    return <>

        <Breadcrumb2Container
            passos={passosInterno.current.keysToList()}
            exibirBotaoLimpar={true}
            exibirBotaoVoltar={true}
            onPassoSelecionado={onPassoSelecionado}
            onBotaoVoltarClicado={onVoltar}
            onBotaoLimparClicado={onLimpar}
            id="CadastrarAtendimentoBreadcrumb"
        />

        <CadastrarAtendimento
            key={limparCadastroAtendimento}
            vendedores={vendedores}
            dadosCadastrarAtendimento={cadastroAtendimento}
            atividades={atividades}
            motivos={motivos}
            onErrorValidacao={(erroFormulario) => globalMU.showErrorMessage(erroFormulario)}
            onSalvar={onSalvarAtendimento}
            onPesquisarMotivos={onListarMotivos}
            onPesquisarAtividades={onListarAtividades}
            onPesquisarVendedor={onListarPorNomeVendedor}
            onLimparCliente={() => setCadastroAtendimento({ ...cadastroAtendimento, NomeCliente: null, CodigoCliente: null })}
            onPesquisarCliente={onPesquisarCliente}
            possuiCliente={!orcamento.Cliente?.Codigo}

        />
    </>
}
